import { Component } from '@theme/component';

/**
 * Dashboard Controller Component
 * Extends the theme's base Component to handle overall dashboard data fetching and UI updates.
 */
class DashboardController extends Component {
  constructor() {
    super();
    this.cache = new Map();
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.loadDashboardData();
  }

  async loadDashboardData() {
    this.classList.add('opacity-50', 'pointer-events-none', 'transition-opacity', 'duration-300');
    
    // Set recent activity back to loading state (useful for retries)
    const listContainer = this.querySelector('[data-activity-list]');
    if (listContainer) {
      listContainer.innerHTML = `
        <tr>
          <td colspan="7" class="text-center py-12 text-gray-400">
            <div class="flex flex-col items-center justify-center gap-3">
              <span class="material-symbols-outlined text-3xl text-gray-600 animate-custom-spin" aria-hidden="true">progress_activity</span>
              <p class="text-xs uppercase tracking-wider text-gray-500 font-bold">Loading activity history...</p>
            </div>
          </td>
        </tr>
      `;
    }

    const TIMEOUT_MS = 8000; // 8 seconds reasonable timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const customerId = this.getAttribute('data-customer-id');
      const url = customerId ? `/apps/api/dashboard?customerId=${encodeURIComponent(customerId)}` : '/apps/api/dashboard';
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.cache.set('dashboardData', data);
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.updateUI(data));
      } else {
        this.updateUI(data);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Failed to load dashboard data:', error);
      this.handleLoadError(error);
    } finally {
      this.classList.remove('opacity-50', 'pointer-events-none');
    }
  }

  handleLoadError(error) {
    this.showErrorToast('Failed to load dashboard data, please try again later');

    const listContainer = this.querySelector('[data-activity-list]');
    if (listContainer) {
      listContainer.innerHTML = `
        <tr>
          <td colspan="7" class="text-center py-12 text-gray-400">
            <div class="flex flex-col items-center justify-center gap-2">
              <span class="material-symbols-outlined text-3xl text-[#ee2b2b]" aria-hidden="true">error</span>
              <p class="text-xs uppercase tracking-wider text-gray-500 font-bold">Failed to load activity history</p>
              <button class="mt-2 text-[10px] text-white bg-white/5 border border-white/10 hover:bg-white/10 px-3 py-1.5 rounded font-bold uppercase tracking-wider transition-colors cursor-pointer pointer-events-auto" type="button" data-retry-btn>
                Retry
              </button>
            </div>
          </td>
        </tr>
      `;

      const retryBtn = listContainer.querySelector('[data-retry-btn]');
      if (retryBtn) {
        retryBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.loadDashboardData();
        });
      }
    }
  }

  showErrorToast(message) {
    let container = document.getElementById('dashboard-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'dashboard-toast-container';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className = 'dashboard-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');

    toast.innerHTML = `
      <span class="material-symbols-outlined text-[#ee2b2b] shrink-0" aria-hidden="true">error</span>
      <div class="flex-1 min-w-0">
        <p class="text-xs font-bold text-white uppercase tracking-wider">Error</p>
        <p class="text-[11px] text-gray-300 mt-1 leading-relaxed">${message}</p>
      </div>
      <button class="text-gray-500 hover:text-white transition-colors shrink-0 flex items-center justify-center p-0.5 pointer-events-auto" aria-label="Close message" type="button" data-toast-close>
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    `;

    const closeBtn = toast.querySelector('[data-toast-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 400);
      });
    }

    container.appendChild(toast);

    // Trigger visual entry transition
    requestAnimationFrame(() => {
      // Force layout recalculation
      toast.offsetHeight;
      toast.classList.add('show');
    });

    // Auto dismiss after 6 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 400);
      }
    }, 6000);
  }

  updateUI(data) {
    console.log('DashboardController: Updating UI with mock data:', data);
    this.stats = data.stats;
    const activeGiveaways = (data.activeGiveaways || []).filter(giveaway => giveaway.status == null || giveaway.status.toUpperCase() === 'ACTIVE');
    this.giveaways = activeGiveaways;

    this.updateStatsSummary(data.stats);
    this.updateMembership(data.membership);
    this.updateActiveGiveaways(activeGiveaways);
    this.updateRecentActivity(data.recentActivity);
    if (data.rewards) this.updateRewards(data.rewards);

    // Initialize the giveaway selector dropdown
    this.initGiveawayDropdown();
  }

  updateStatsSummary(stats) {
    const setContent = (selector, value) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = value;
    };

    setContent('[data-stat-total-entries]', stats.totalActiveEntries.toLocaleString());
    setContent('[data-stat-entries-trend]', stats.entriesTrend);
    setContent('[data-stat-multiplier]', stats.currentMultiplier);
    setContent('[data-stat-multiplier-status]', stats.multiplierStatus);
    setContent('[data-stat-probability]', stats.winProbability);
    setContent('[data-stat-probability-note]', stats.probabilityNote);
    setContent('[data-stat-member-since]', stats.memberSince);
    setContent('[data-stat-member-tier]', stats.memberTier);
  }

  initGiveawayDropdown() {
    const selectEl = this.querySelector('[data-giveaway-select]');
    if (!selectEl) return;

    // Populate dropdown with active giveaways
    selectEl.innerHTML = '<option value="total">All Active Giveaways</option>';
    this.giveaways.forEach((giveaway, index) => {
      const option = document.createElement('option');
      option.value = index.toString();
      option.textContent = giveaway.title;
      selectEl.appendChild(option);
    });

    // Add change event listener
    if (!this.hasSelectListener) {
      selectEl.addEventListener('change', (e) => {
        const value = e.target.value;
        if (value === 'total') {
          this.selectTotalEntries();
        } else {
          this.selectGiveaway(parseInt(value, 10));
        }
      });
      this.hasSelectListener = true;
    }

    // Default to total on load
    this.selectTotalEntries();
  }

  selectTotalEntries() {
    if (!this.stats) return;

    // 1. Update the "Total Active Entries" value in Stats card
    const entriesEl = this.querySelector('[data-stat-total-entries]');
    if (entriesEl) {
      entriesEl.textContent = this.stats.totalActiveEntries.toLocaleString();
    }

    // 2. Update the trend/context subtext to show the overall trend
    const trendEl = this.querySelector('[data-stat-entries-trend]');
    if (trendEl) {
      trendEl.textContent = this.stats.entriesTrend;
      trendEl.className = 'text-green-500 text-xs font-bold';
    }

    // 3. Reset the label text
    const labelEl = this.querySelector('[data-active-entries-label]');
    if (labelEl) {
      labelEl.textContent = labelEl.getAttribute('data-original-label') || 'Total Active Entries';
    }

    // 4. Update the dropdown value to sync
    const selectEl = this.querySelector('[data-giveaway-select]');
    if (selectEl) {
      selectEl.value = 'total';
    }

    // 5. Remove grid selection highlights
    const cards = this.querySelectorAll('[data-giveaway-card]');
    cards.forEach(card => {
      card.className = 'bg-[#161616] border-[#262626] rounded-xl overflow-hidden flex flex-col md:flex-row h-auto md:h-48 w-full md:w-[500px] md:shrink-0 group hover:border-[#ee2b2b]/50 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer';
      const targetBadge = card.querySelector('.selected-target-badge');
      if (targetBadge) {
        targetBadge.remove();
      }
    });

    this.selectedGiveawayIndex = null;
  }

  updateMembership(membership) {
    const setContent = (selector, value) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = value;
    };

    setContent('[data-tier-name]', membership.tierName);
    setContent('[data-tier-renewal]', membership.renewalDate);
    setContent('[data-tier-badge]', membership.tierBadge);
    setContent('[data-benefit-credits]', membership.credits);
  }

  updateActiveGiveaways(giveaways) {
    const gridContainer = this.querySelector('[data-active-giveaways]');
    if (!gridContainer) return;

    const getMoreUrlEl = this.querySelector('[data-get-more-url]');
    const getMoreUrl = getMoreUrlEl ? getMoreUrlEl.getAttribute('data-get-more-url') : '/collections/all';
    const showProgress = getMoreUrlEl ? getMoreUrlEl.getAttribute('data-show-progress') !== 'false' : true;

    // Clear existing cards
    gridContainer.innerHTML = '';
    const activeGiveaways = (giveaways || []).filter(giveaway => giveaway.status == null || giveaway.status.toUpperCase() === 'ACTIVE');
    this.giveaways = activeGiveaways;

    // If there are no active giveaways, render the empty state
    if (activeGiveaways.length === 0) {
      gridContainer.innerHTML = `
        <div class="col-span-full py-16 px-6 flex flex-col items-center justify-center text-center bg-[#161616] border border-[#262626] rounded-xl gap-3 w-full shadow-lg" data-no-giveaways-message>
          <span class="material-symbols-outlined text-4xl text-gray-600 animate-pulse" aria-hidden="true">local_fire_department</span>
          <h3 class="text-sm font-bold text-gray-300 uppercase tracking-wider">No Active Giveaways</h3>
          <p class="text-xs text-gray-500 max-w-sm leading-relaxed">There are no active giveaways running at the moment. Check back soon for our next draw!</p>
        </div>
      `;
      return;
    }

    activeGiveaways.forEach((giveaway, index) => {
      const card = document.createElement('div');
      card.className = 'bg-[#161616] border-[#262626] rounded-xl overflow-hidden flex flex-col md:flex-row h-auto md:h-48 w-full md:w-[500px] md:shrink-0 group hover:border-[#ee2b2b]/50 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer';
      card.style.minWidth = 'min(500px, 100%)';
      card.dataset.giveawayCard = index;
      
      card.addEventListener('click', () => {
        this.selectGiveaway(index);
      });

      let imageUrl = giveaway.image || '';
      const isVideo = (giveaway.imageType && giveaway.imageType.toUpperCase() === 'VIDEO') || 
                      (giveaway.image_type && giveaway.image_type.toUpperCase() === 'VIDEO');
      if (isVideo) {
        imageUrl = giveaway.previewUrl || giveaway.preview_url || giveaway.videoPreviewUrl || giveaway.video_preview_url || '';
        if (!imageUrl && giveaway.images && Array.isArray(giveaway.images)) {
          const firstImg = giveaway.images.find(img => img.type === 'IMAGE') || giveaway.images.find(img => img.previewUrl || img.preview_url);
          if (firstImg) {
            imageUrl = firstImg.url || firstImg.previewUrl || firstImg.preview_url || '';
          }
        }
      }

      let days = 0;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      let showCountdown = false;

      if (giveaway.timeRemaining) {
        days = typeof giveaway.timeRemaining.days === 'number' ? giveaway.timeRemaining.days : 0;
        hours = typeof giveaway.timeRemaining.hours === 'number' ? giveaway.timeRemaining.hours : 0;
        minutes = typeof giveaway.timeRemaining.minutes === 'number' ? giveaway.timeRemaining.minutes : 0;
        seconds = typeof giveaway.timeRemaining.seconds === 'number' ? giveaway.timeRemaining.seconds : 0;
      }

      const endTimestamp = giveaway.end_ts || giveaway.end_date || giveaway.endDate;
      if (endTimestamp) {
        const diffMs = new Date(endTimestamp).getTime() - Date.now();
        if (diffMs > 0) {
          days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
          hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        } else {
          days = 0; hours = 0; minutes = 0; seconds = 0;
        }
      } else {
        if (hours >= 24 && !days) {
          days = Math.floor(hours / 24);
          hours = hours % 24;
        }
      }

      const hasTimingInfo = !!(giveaway.timeRemaining || endTimestamp);
      showCountdown = hasTimingInfo && (days <= 30) && (days > 0 || hours > 0 || minutes > 0 || seconds > 0);

      card.innerHTML = `
        <div class="w-full h-48 md:w-1/3 md:h-full bg-cover bg-center bg-no-repeat relative shrink-0" style='background-image: url("${imageUrl}");'>
          ${giveaway.badge ? `<div class="absolute top-3 left-3 bg-[#ee2b2b] text-[9px] font-black px-1.5 py-0.5 rounded text-white tracking-widest uppercase shadow-md">${giveaway.badge}</div>` : ''}
        </div>
        <div class="w-full md:w-2/3 p-4 flex flex-col justify-between flex-grow gap-2.5 overflow-hidden">
          <div>
            <div class="flex justify-between items-start mb-1 gap-2">
              <h3 class="font-bold text-white text-sm md:text-base group-hover:text-[#ee2b2b] transition-colors line-clamp-1">${giveaway.title}</h3>
              <a href="/pages/current-giveaway" onclick="event.stopPropagation();" class="text-gray-500 hover:text-white transition-colors shrink-0 flex items-center justify-center" aria-label="View current giveaway">
                <span class="material-symbols-outlined text-sm md:text-base" aria-hidden="true">open_in_new</span>
              </a>
            </div>
            <p class="text-[9px] text-gray-400 uppercase tracking-widest font-semibold truncate">${giveaway.specs}</p>
          </div>
          
          <div class="space-y-1.5 bg-[#0d0d0d] p-2.5 rounded-lg border border-white/5">
            <div class="flex justify-between text-[11px] font-semibold">
              <span class="text-gray-400">Your Entries:</span>
              <span class="text-[#ee2b2b] font-bold" data-entries-count="${giveaway.entries}">${giveaway.entries.toLocaleString()}</span>
            </div>
            ${showProgress ? `
            <div class="w-full bg-black h-1.5 rounded-full overflow-hidden" role="progressbar" aria-valuenow="${giveaway.progressPercent}" aria-valuemin="0" aria-valuemax="100">
              <div class="bg-gradient-to-r from-[#ee2b2b] to-[#ff4444] h-full" data-progress-bar style="width: ${giveaway.progressPercent}%"></div>
            </div>
            <div class="flex justify-between text-[9px] text-gray-500 font-medium">
              <span>Goal Progress</span>
              <span data-progress-text>${giveaway.progressPercent}%</span>
            </div>
            ` : ''}
          </div>

          <div class="flex items-center justify-between pt-2 border-t border-[#222] mt-auto gap-3">
            ${showCountdown ? `
            <div class="flex gap-1 justify-start shrink-0" aria-label="Time remaining">
              <div class="bg-black/50 px-1.5 py-0.5 rounded text-center border border-white/5 min-w-[32px]">
                <p class="text-[11px] font-bold leading-none text-white font-mono" data-countdown-days>${days.toString().padStart(2, '0')}</p>
                <p class="text-[7px] text-gray-500 uppercase font-semibold mt-0.5">days</p>
              </div>
              <div class="bg-black/50 px-1.5 py-0.5 rounded text-center border border-white/5 min-w-[32px]">
                <p class="text-[11px] font-bold leading-none text-white font-mono" data-countdown-hours>${hours.toString().padStart(2, '0')}</p>
                <p class="text-[7px] text-gray-500 uppercase font-semibold mt-0.5">hrs</p>
              </div>
              <div class="bg-black/50 px-1.5 py-0.5 rounded text-center border border-white/5 min-w-[32px]">
                <p class="text-[11px] font-bold leading-none text-white font-mono" data-countdown-minutes>${minutes.toString().padStart(2, '0')}</p>
                <p class="text-[7px] text-gray-500 uppercase font-semibold mt-0.5">min</p>
              </div>
              <div class="bg-black/50 px-1.5 py-0.5 rounded text-center border border-white/5 min-w-[32px]">
                <p class="text-[11px] font-bold leading-none text-white font-mono" data-countdown-seconds>${seconds.toString().padStart(2, '0')}</p>
                <p class="text-[7px] text-gray-500 uppercase font-semibold mt-0.5">sec</p>
              </div>
            </div>
            ` : '<div class="flex-grow"></div>'}
            <a href="${getMoreUrl}" onclick="event.stopPropagation();" class="bg-[#ee2b2b] hover:bg-[#ff3b3b] text-white text-[9px] font-bold py-1.5 px-3 rounded uppercase tracking-wider transition-all duration-200 transform active:scale-95 shrink-0 flex items-center justify-center no-underline">Get More</a>
          </div>
        </div>
      `;
      
      gridContainer.appendChild(card);
    });
  }

  selectGiveaway(index) {
    if (!this.giveaways || this.giveaways.length === 0) return;
    const selectedGiveaway = this.giveaways[index];
    if (!selectedGiveaway) return;

    this.selectedGiveawayIndex = index;

    // 1. Update the "Total Active Entries" value in Stats card
    const entriesEl = this.querySelector('[data-stat-total-entries]');
    if (entriesEl) {
      entriesEl.textContent = selectedGiveaway.entries.toLocaleString();
    }

    // 2. Update the trend/context subtext to show the active giveaway name
    const trendEl = this.querySelector('[data-stat-entries-trend]');
    if (trendEl) {
      trendEl.textContent = `For ${selectedGiveaway.title}`;
      trendEl.className = 'text-[#ee2b2b] text-xs font-bold uppercase tracking-wider block mt-1 animate-pulse';
    }

    // 3. Update the label to "Giveaway Entries"
    const labelEl = this.querySelector('[data-active-entries-label]');
    if (labelEl) {
      labelEl.textContent = 'Giveaway Entries';
    }

    // 4. Update the dropdown value to sync
    const selectEl = this.querySelector('[data-giveaway-select]');
    if (selectEl) {
      selectEl.value = index.toString();
    }

    // 5. Update the visual selected states on the cards in the grid
    const cards = this.querySelectorAll('[data-giveaway-card]');
    cards.forEach((card, cardIndex) => {
      if (cardIndex === index) {
        // Selected style with red glow, red-tinted background, and vibrant red border
        card.className = 'bg-[#1c1213] border-[#ee2b2b] rounded-xl overflow-hidden flex flex-col md:flex-row h-auto md:h-48 w-full md:w-[500px] md:shrink-0 group transition-all duration-300 shadow-[0_0_20px_rgba(238,43,43,0.15)] cursor-pointer';
        
        // Add "ACTIVE TARGET" check badge in top right
        let targetBadge = card.querySelector('.selected-target-badge');
        if (!targetBadge) {
          targetBadge = document.createElement('div');
          targetBadge.className = 'selected-target-badge absolute top-3 right-3 bg-[#ee2b2b] text-[9px] font-black px-1.5 py-0.5 rounded text-white tracking-widest uppercase flex items-center gap-1 shadow-md';
          targetBadge.innerHTML = `<span class="material-symbols-outlined text-[10px]" style="font-size: 10px; font-weight: 900;">check_circle</span> Active Target`;
          const imgContainer = card.querySelector('.relative');
          if (imgContainer) {
            imgContainer.appendChild(targetBadge);
          }
        }
      } else {
        // Normal card style
        card.className = 'bg-[#161616] border-[#262626] rounded-xl overflow-hidden flex flex-col md:flex-row h-auto md:h-48 w-full md:w-[500px] md:shrink-0 group hover:border-[#ee2b2b]/50 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer';
        
        // Remove "ACTIVE TARGET" badge
        const targetBadge = card.querySelector('.selected-target-badge');
        if (targetBadge) {
          targetBadge.remove();
        }
      }
    });
  }

  updateRecentActivity(activities) {
    const listContainer = this.querySelector('[data-activity-list]');
    if (!listContainer) return;

    // Clear existing static rows
    listContainer.innerHTML = '';

    activities.forEach(activity => {
      const row = document.createElement('tr');
      row.className = 'hover:bg-white/5 transition-colors';
      row.innerHTML = `
        <td class="text-gray-400" data-activity-date>${activity.date}</td>
        <td>
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined ${activity.type === 'SUBSCRIPTION' ? 'text-blue-400' : activity.type === 'REFERRAL' ? 'text-gray-500' : 'text-[#ee2b2b]'} text-sm" aria-hidden="true">
              ${activity.type === 'SUBSCRIPTION' ? 'stars' : activity.type === 'REFERRAL' ? 'local_activity' : 'shopping_bag'}
            </span>
            <span class="font-medium" data-activity-name>${activity.source}</span>
          </div>
        </td>
        <td class="font-mono text-[10px] text-gray-500" data-activity-id>${activity.id}</td>
        <td><span class="bg-white/5 px-2 py-0.5 rounded text-[10px]" data-activity-type>${activity.type}</span></td>
        <td class="text-right font-bold text-white" data-activity-entries>${activity.entries}</td>
        <td class="text-right text-gray-400" data-activity-multiplier>${activity.multiplier}</td>
        <td class="text-right"><span class="text-green-500 text-[10px] font-bold" data-activity-status>${activity.status}</span></td>
      `;
      listContainer.appendChild(row);
    });
  }

  updateRewards(rewards) {
    const rewardsContainer = this.querySelector('[data-rewards-container]');
    if (!rewardsContainer) return;

    rewardsContainer.innerHTML = '';
    
    rewards.forEach(reward => {
      const rewardEl = document.createElement('div');
      rewardEl.className = `${reward.bgColor} border ${reward.active ? 'border-[#ee2b2b]/20 group' : 'border-[#262626] hover:border-white/20 transition-colors'} p-4 rounded-xl flex items-center gap-4 ${reward.claimable ? 'group relative overflow-hidden' : ''}`;
      
      let innerHTML = `
        <div class="size-10 ${reward.iconBg} rounded-lg flex items-center justify-center ${reward.iconColor} shrink-0">
          <span class="material-symbols-outlined" aria-hidden="true">${reward.icon}</span>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold truncate">${reward.title}</p>
          <p class="text-[10px] ${reward.statusColor} font-bold ${reward.active ? 'uppercase' : ''}">${reward.status}</p>
        </div>
      `;

      if (reward.active) {
        innerHTML += `<span class="material-symbols-outlined text-xs text-[#ee2b2b]" aria-label="Active">check_circle</span>`;
      }
      
      if (reward.claimable) {
        innerHTML += `
          <button type="button" class="absolute inset-0 bg-[#ee2b2b]/90 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" data-claim-reward-btn="${reward.id}">
            <span class="text-xs font-bold uppercase tracking-wider text-white">Claim Now</span>
          </button>
        `;
      }

      rewardEl.innerHTML = innerHTML;
      rewardsContainer.appendChild(rewardEl);
    });
  }
}

customElements.define('dashboard-controller', DashboardController);